/**
 * @fileoverview App shell for the organization-admin portal (Spec 11). Mirrors
 * the instructor StudioLayout — fixed 256px left rail on md+, top bar + drawer
 * on mobile — with the org's day-to-day pages in the nav and, like the
 * instructor rail, Settings pinned to the bottom above Hans Academy and Sign
 * out. Settings holds Plan, Admins, Branding, Features, Schedule and Activity
 * (see lib/orgSettings.js). Reporting is the Dashboard itself, so it has no
 * item of its own.
 *
 * The identity in the corner is the org's OWN, read from the stored
 * `org.branding` rather than the resolved `branding` the auth payload also
 * carries. The resolved brand answers "whose mark do this org's INSTRUCTORS and
 * emails wear", which is add-on gated and therefore says "Hans" for most orgs —
 * the wrong answer for the admin of Westview Music looking at Westview's own
 * portal. `orgIdentity` asks the other question: whose portal is this.
 *
 * Under the org's name is the person holding the session. An organization has
 * many administrators and one portal, and the shell used to say only which
 * organization it was — so a shared office machine, or a director who also
 * keeps a second login, gave no way to tell WHO was signed in before acting as
 * them. `adminIdentity` answers that; the org name above it still answers
 * whose portal this is.
 */

import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Icon } from "@/components/studio/primitives";
import { OrgAuthContext } from "@/App";
import { ORG_SETTINGS_PATH } from "@/lib/orgSettings";

const SIDEBAR_W = 256;

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: "home", path: "/org" },
  { id: "instructors", label: "Instructors", icon: "users", path: "/org/instructors" },
  { id: "usage", label: "Active students", icon: "graduation", path: "/org/usage" },
  { id: "calendar", label: "Calendar", icon: "calendar", path: "/org/calendar" },
  { id: "programs", label: "Programs", icon: "grid", path: "/org/programs" },
];

const SETTINGS_NAV = { id: "settings", label: "Settings", icon: "settings", path: ORG_SETTINGS_PATH };

/* A program viewer (a sponsor, a school coordinator) holds an org session but
   runs nothing: one destination, the programs they were given. Every admin
   route refuses them server-side; this only stops offering the doors. */
const VIEWER_NAV = [{ id: "viewer", label: "My programs", icon: "grid", path: "/org/viewer" }];

/* A viewer's Profile holds their account, Hans itself and the Academy's
   tutorials for viewers, so it takes Hans Academy's place in their rail. */
const PROFILE_NAV = { id: "profile", label: "Profile", icon: "user", path: "/org/viewer/profile" };

export function navFor(admin) {
  return admin?.role === "viewer" ? VIEWER_NAV : NAV;
}

/** Settings is for administrators; a viewer has nothing there to set. */
export function showsSettings(admin) {
  return admin?.role !== "viewer";
}

export function activeNavId(pathname) {
  if (pathname.startsWith("/org/viewer/profile")) return "profile";
  if (pathname.startsWith("/org/viewer")) return "viewer";
  if (pathname.startsWith("/org/instructors")) return "instructors";
  if (pathname.startsWith("/org/calendar")) return "calendar";
  if (pathname.startsWith("/org/programs")) return "programs";
  if (pathname.startsWith("/org/classes")) return "programs";
  if (pathname.startsWith("/org/usage")) return "usage";
  if (pathname.startsWith(ORG_SETTINGS_PATH)) return "settings";
  return "dashboard";
}

/**
 * The org's own name + logo, never the house brand. `display_name` is what the
 * admin typed on the Branding page; `name` is what they signed up as.
 */
export function orgIdentity(org) {
  return {
    name: org?.branding?.display_name || org?.name || "Organization",
    logoUrl: org?.branding?.logo_url || null,
  };
}

/**
 * The signed-in administrator, as a name to put under the org's.
 *
 * The email is the fallback rather than a placeholder because it is the thing
 * that actually identifies the session: an admin added by invitation has no
 * name until they fill one in, and "Administrator" under the org name would
 * say nothing that the portal itself did not already say.
 */
export function adminIdentity(admin) {
  if (!admin) return null;
  const full = [admin.first_name, admin.last_name].filter(Boolean).join(" ").trim();
  return full || admin.name || admin.email || null;
}

/**
 * The org's mark on its tile: the rail's corner, and wherever a viewer's
 * programs carry the organization's logo.
 *
 * Two fixed tiles, identical in both themes, because neither subject is ours
 * to re-colour. An UPLOADED logo is someone else's artwork and gets white —
 * the ground nearly every logo is drawn for, and the one that keeps a dark-ink
 * mark legible on our dark shell. The fallback glyph keeps the true black it
 * was drawn against, which is why it can hold the bright yellow FILL colour
 * rather than the gold-ink text token (dim on black). Contained, not cropped:
 * a logo is a shape, and `object-cover` would slice the ends off a wordmark.
 */
export function OrgLogoTile({ logoUrl, className = "h-[42px] w-[42px]", testId = "org-logo" }) {
  return (
    <div
      className={[
        "flex shrink-0 items-center justify-center overflow-hidden rounded-[12px]",
        "border border-[var(--studio-divider-strong)]",
        logoUrl ? "bg-white p-1" : "bg-black", // brand-lint-ignore: fixed logo tiles, see above
        className,
      ].join(" ")}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" data-testid={testId} className="h-full w-full object-contain" />
      ) : (
        <Icon name="users" size={22} strokeWidth={2} className="text-[var(--accent-yellow)]" />
      )}
    </div>
  );
}

function NavItem({ item, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.path)}
      aria-current={active ? "page" : undefined}
      data-testid={`org-nav-${item.id}`}
      className={[
        "flex w-full items-center gap-[14px] rounded-[12px] border px-[14px] py-[11px] text-left transition-colors duration-150",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]"
          : "border-transparent hover:bg-[var(--studio-raised)]",
      ].join(" ")}
    >
      <Icon
        name={item.icon}
        size={20}
        strokeWidth={active ? 2 : 1.7}
        className={active ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]"}
      />
      <span
        className={[
          "flex-1 font-sans text-base",
          active ? "font-semibold text-[var(--text-primary)]" : "font-medium text-[var(--text-secondary)]",
        ].join(" ")}
      >
        {item.label}
      </span>
      {active && <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-yellow)]" />}
    </button>
  );
}

function SidebarInner({ onNavigate }) {
  const { org, admin, logout } = useContext(OrgAuthContext) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeNavId(location.pathname);

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const { name: orgName, logoUrl } = orgIdentity(org);
  const signedInAs = adminIdentity(admin);

  return (
    <div className="flex h-full flex-col px-4 pb-5 pt-[26px]">
      {/* Brand */}
      <div className="flex items-center gap-3 px-2 pb-[22px]">
        <OrgLogoTile logoUrl={logoUrl} />
        <div className="min-w-0">
          {/* A long legal name truncates at 256px — the tooltip is how it can
              still be read in full without the rail growing. */}
          <div title={orgName} className="truncate font-sans text-lg font-bold leading-none text-[var(--text-primary)]">
            {orgName}
          </div>
          {/* The person, not the account type. "Organization" said nothing the
              nav below did not — and an email is long, so it truncates with
              the same tooltip the org name gets. */}
          <div
            title={signedInAs || undefined}
            data-testid="org-signed-in-as"
            className="mt-[3px] truncate font-sans text-[13px] text-[var(--text-tertiary)]"
          >
            {signedInAs || "Organization"}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1">
        {navFor(admin).map((item) => (
          <NavItem key={item.id} item={item} active={active === item.id} onSelect={go} />
        ))}
      </nav>

      <div className="flex-1" />

      {/* Settings, Help and Sign out pinned to the bottom */}
      <div className="border-t border-[var(--studio-divider)] pt-3">
        {showsSettings(admin) && <NavItem item={SETTINGS_NAV} active={active === "settings"} onSelect={go} />}
        {admin?.role === "viewer" ? (
          <NavItem item={PROFILE_NAV} active={active === "profile"} onSelect={go} />
        ) : (
          /* A new tab, deliberately: the guide is a PUBLIC path (and therefore
             always dark), so navigating in place would swap the admin's portal
             for the marketing shell and lose the page they were on. */
          <a
            href="/org-guide"
            target="_blank"
            rel="noreferrer"
            data-testid="org-guide-link"
            onClick={() => onNavigate?.()}
            className="flex w-full items-center gap-[14px] rounded-[12px] border border-transparent px-[14px] py-[11px] text-left transition-colors duration-150 hover:bg-[var(--studio-raised)]"
          >
            <Icon name="book" size={20} strokeWidth={1.7} className="text-[var(--text-secondary)]" />
            <span className="font-sans text-base font-medium text-[var(--text-secondary)]">Hans Academy</span>
          </a>
        )}
        <button
          type="button"
          onClick={() => logout?.()}
          data-testid="org-signout"
          className="flex w-full items-center gap-[14px] rounded-[12px] border border-transparent px-[14px] py-[11px] text-left transition-colors duration-150 hover:bg-[var(--studio-raised)]"
        >
          <Icon name="logout" size={20} strokeWidth={1.7} className="text-[var(--text-secondary)]" />
          <span className="font-sans text-base font-medium text-[var(--text-secondary)]">Sign out</span>
        </button>
      </div>
    </div>
  );
}

export default function OrgLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { org } = useContext(OrgAuthContext) || {};
  const { name: orgName } = orgIdentity(org);

  useEffect(() => {
    setDrawerOpen(false);
    document.getElementById("org-main")?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="studio min-h-screen bg-[var(--studio-base)] text-[var(--text-primary)]">
      {/* Desktop rail */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden border-r border-[var(--studio-divider)] bg-[var(--studio-sidebar)] md:block"
        style={{ width: SIDEBAR_W }}
      >
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-[var(--studio-divider)] bg-[var(--studio-sidebar)] px-4 md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          data-testid="org-menu"
          onClick={() => setDrawerOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-[12px] text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
        >
          <Icon name="menu" size={22} />
        </button>
        <span title={orgName} className="truncate font-sans text-base font-bold text-[var(--text-primary)]">
          {orgName}
        </span>
        <span className="w-10" />
      </header>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="studio w-[256px] border-[var(--studio-divider)] bg-[var(--studio-sidebar)] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarInner onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main id="org-main" className="min-h-screen pt-14 md:ml-64 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
